import React, { useEffect, useState } from 'react';

import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableSchedule,
  RequestScheduleInterFace,
} from '../../interface/schedule-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import {
  fetchAllSchedules,
  setScheduleFilterData,
  updateScheduleFilters,
  fetchListFilters,
  setSortData,
} from '../../redux/slice/ScheduleManagementSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import TopHeaderButtons from '../../shared-components/top-header-buttons/TopHeaderButtons';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import { CapitalizeFirstLetter } from '../../shared-functions/StringFunctions';
import scheduleApi from '../../utils/api/schedule';

import DeviceFilter from './components/DeviceFilter';
import ScheduleAvailability from './components/ScheduleAvailability';

/**
 * Schedule management component
 */
function ScheduleManagement() {
  const dispatch = useAppDispatch();
  const { schedules, page, limit, loading, totalPages, totalResults, filters, filterListLoading } =
    useAppSelector((state) => state.schedules);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [modalView, setModalView] = useState<{
    status: boolean;
    title: string;
    content: JSX.Element;
    header: boolean;
  }>({
    status: false,
    title: '',
    content: <div />,
    header: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { sortBy, sortField } = useAppSelector((state) => state.schedules);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestScheduleInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllSchedules({
          ...data,
        }),
      );
    }
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: '' as keyof AvailableSchedule,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (schedule: AvailableSchedule, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: 'Booking Date',
      accessorKey: 'startDateUtc' as keyof AvailableSchedule,
      sortAllow: true,
    },
    {
      label: 'Booking Time',
      accessorKey: 'time' as keyof AvailableSchedule,
      // sortAllow: true,
    },
    {
      label: 'Station Name',
      accessorKey: 'chargingStation' as keyof AvailableSchedule,
      sortAllow: true,
    },
    {
      label: 'Station Location',
      accessorKey: 'chargingStationAddress' as keyof AvailableSchedule,
      sortAllow: true,
      /**
       * Location view in table
       * @param schedule
       * @param index
       */
      customData: (schedule: AvailableSchedule) =>
        schedule.chargingStationAddress ? (
          <div className="location-in-schedule-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{schedule.chargingStationAddress}</p>
          </div>
        ) : null,
    },
    {
      label: 'Vehicle Details',
      accessorKey: 'vehicleModel' as keyof AvailableSchedule,
      sortAllow: true,
    },
    {
      label: 'User Name',
      accessorKey: 'user' as keyof AvailableSchedule,
      sortAllow: true,
    },
    {
      label: 'User Id',
      accessorKey: 'userCustomId' as keyof AvailableSchedule,
      // sortAllow: true,
    },
    {
      label: 'Status',
      accessorKey: 'status' as keyof AvailableSchedule,
      columnWidth: 60,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (schedule: AvailableSchedule) => (
        <div className="status-in-schedule-table">
          <p
            className={`table-row-each-data ${schedule.status === 'confirmed' ? 'active' : 'in-active'}`}>
            {CapitalizeFirstLetter(schedule.status)}
          </p>
        </div>
      ),
    },
  ];

  /**
   * Close the modal function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setScheduleFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Open the device filter modal function
   */
  const deviceFilterModal = () => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: <DeviceFilter closeModal={closeModal} />,
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    scheduleApi
      .fetchSchedulesExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Booking Date',
          'Booking Time',
          'Station Name',
          'Station Location',
          'Vehicle Details',
          'User Name',
          'User Id',
          'Status',
        ];
        const rows = response.data.results.map((row: AvailableSchedule) => [
          row.startDateUtc ? ConvertUTCtoDate(row.startDateUtc) : '',
          row.startDateUtc
            ? `${FormatUtcString(ConvertUTCtoHHMM(row.startDateUtc))} - ${FormatUtcString(ConvertUTCtoHHMM(row.endDateUtc))}`
            : '',
          row.chargingStation || '',
          row.chargingStationAddress || '',
          row.vehicleModel || '',
          row.user || '',
          row.userCustomId || '',
          row.status ? CapitalizeFirstLetter(row.status) : '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'schedule-list.csv');
          } else {
            exportPDF(headers, rows, 'schedule-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="schedule-management-wrap">
      <ModalBox
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        open={modalView.status}
        header={modalView.header}
      />
      <div className="schedule-management-header">
        <PageTitle title="Charging Schedule Management" />
      </div>
      <div className="schedule-availability-wrap">
        <TopHeaderButtons
          filterFunction={deviceFilterModal}
          filterClearFunction={() => {}}
          sizeChangeButtonRequired={false}
        />
        <ScheduleAvailability />
        <div className="schedule-management-header">
          <PageTitle title="Scheduling Details" />
        </div>
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={schedules}
          currentPage={page}
          tableHeight="70vh"
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={schedules.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateScheduleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default ScheduleManagement;
